(function () {
  // -----------------------------
  // Utilities
  // -----------------------------
  function retry(fn, { attempts = 20, delay = 200 } = {}) {
    let count = 0;

    function run() {
      if (fn() || count >= attempts) return;
      count++;
      setTimeout(run, delay);
    }

    run();
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  // -----------------------------
  // Gift Note
  // -----------------------------
  const GIFT_NOTE = {
    property: 'Gift note',
    // Set to true if the Upcart app is ever removed. Upcart patches window.fetch
    // and opens its drawer on any /cart/add.js call, so nothing is needed while
    // it is installed. Without it, nothing would signal success to the customer.
    redirectAfterAdd: false,
    selectors: {
      wrapper: '[data-gift-note]',
      checkbox: '[data-gift-note-checkbox]',
      panel: '[data-gift-note-panel]',
      text: '[data-gift-note-text]',
      count: '[data-gift-note-count]',
      msg: '[data-gift-note-msg]',
      form: 'form[action*="/cart/add"]',
      submit: '[type="submit"], [name="add"]'
    }
  };

  function giftNoteMessage(wrapper, text, isError) {
    const msg = wrapper.querySelector(GIFT_NOTE.selectors.msg);
    if (!msg) return;
    msg.textContent = text;
    msg.classList.toggle('gift-note__msg--error', !!isError);
    msg.hidden = !text;
  }

  function serializeProductForm(form) {
    const item = { quantity: 1, properties: {} };

    new FormData(form).forEach((value, key) => {
      if (key === 'id') {
        item.id = value;
      } else if (key === 'quantity') {
        item.quantity = parseInt(value, 10) || 1;
      } else if (key.startsWith('properties[')) {
        if (value !== '') item.properties[key.slice(11, -1)] = value;
      }
    });

    if (!Object.keys(item.properties).length) delete item.properties;
    return item;
  }

  async function addItemsToCart(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });

    const body = await res.json();
    if (!res.ok) throw new Error(body.description || body.message || 'Add to cart failed');
    return body;
  }

  async function cartHasVariant(variantId) {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      return cart.items.some(item => String(item.variant_id) === String(variantId));
    } catch {
      return false;
    }
  }

  function lockGiftNote(wrapper, message) {
    const checkbox = wrapper.querySelector(GIFT_NOTE.selectors.checkbox);
    const panel = wrapper.querySelector(GIFT_NOTE.selectors.panel);

    if (checkbox) {
      checkbox.checked = false;
      checkbox.disabled = true;
    }
    if (panel) panel.hidden = true;
    if (message) giftNoteMessage(wrapper, message, false);
  }

  // One gift note per order. Enforced in the UI only — a determined user can
  // still bypass it via the AJAX API. Guarded again at the cart level.
  async function initGiftNoteWrapper(wrapper) {
    if (wrapper.hasAttribute('data-gift-note-ready')) return;
    wrapper.setAttribute('data-gift-note-ready', '');

    const { checkbox, panel, text, count } = GIFT_NOTE.selectors;
    const box = wrapper.querySelector(checkbox);
    const pane = wrapper.querySelector(panel);
    const area = wrapper.querySelector(text);
    const counter = wrapper.querySelector(count);

    if (box && pane) {
      box.addEventListener('change', () => {
        pane.hidden = !box.checked;
        giftNoteMessage(wrapper, '', false);
        // Text is preserved when unchecked so re-checking restores it.
        if (box.checked && area) area.focus();
      });
    }

    if (area && counter) {
      const update = () => { counter.textContent = area.value.length; };
      area.addEventListener('input', update);
      update();
    }

    if (await cartHasVariant(wrapper.getAttribute('data-variant-id'))) {
      lockGiftNote(wrapper, 'A gift note is already in your cart.');
    }
  }

  // Document-level capture. The product form is re-rendered on variant change,
  // so binding directly to the button does not survive. Only forms containing a
  // gift note block are intercepted; everything else keeps its native behaviour.
  function initGiftNoteInterceptor() {
    if (document.documentElement.hasAttribute('data-gift-note-bound')) return;
    document.documentElement.setAttribute('data-gift-note-bound', '');

    document.addEventListener('click', async event => {
      const button = event.target.closest(GIFT_NOTE.selectors.submit);
      const form = button?.closest(GIFT_NOTE.selectors.form);
      const wrapper = form?.querySelector(GIFT_NOTE.selectors.wrapper);
      if (!wrapper) return;

      const box = wrapper.querySelector(GIFT_NOTE.selectors.checkbox);
      const area = wrapper.querySelector(GIFT_NOTE.selectors.text);
      const wantsNote = box?.checked && !box.disabled;
      const note = wantsNote && area ? area.value.trim() : '';

      if (!wantsNote) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!note) {
        giftNoteMessage(wrapper, 'Please enter your gift note message.', true);
        area?.focus();
        return;
      }

      const variantId = wrapper.getAttribute('data-variant-id');
      const items = [serializeProductForm(form)];

      button.setAttribute('disabled', 'disabled');
      giftNoteMessage(wrapper, '', false);

      try {
        if (!(await cartHasVariant(variantId))) {
          items.push({
            id: variantId,
            quantity: 1,
            properties: { [GIFT_NOTE.property]: note }
          });
        }

        await addItemsToCart(items);
        lockGiftNote(wrapper);

        if (GIFT_NOTE.redirectAfterAdd) window.location = '/cart';
      } catch (error) {
        giftNoteMessage(wrapper, error.message || 'Something went wrong. Please try again.', true);
      } finally {
        button.removeAttribute('disabled');
      }
    }, true);
  }

  function initGiftNote() {
    initGiftNoteInterceptor();
    document.querySelectorAll(GIFT_NOTE.selectors.wrapper).forEach(initGiftNoteWrapper);
  }

  // -----------------------------
  // AfterSell Style Injection
  // -----------------------------
  function initAfterSellStyles() {
    retry(() => {
      const el = document.querySelector('#aftersell-pdp-upsell');
      if (!el || !el.shadowRoot) return false;

      const root = el.shadowRoot;

      if (root.querySelector('#custom-upsell-style')) return true;

      const style = document.createElement('style');
      style.id = 'custom-upsell-style';

      style.textContent = `
        .pdp-widget .product-info {
          padding-bottom: 0 !important;
        }
        .pdp-widget .product-card-actions {
          align-items: center;
        }
        .pdp-widget .add-button {
          padding: 10px 16px !important;
          height: auto !important;
          width: auto;
        }
      `;

      root.appendChild(style);
      return true;
    }, { attempts: 50, delay: 100 });
  }

  // -----------------------------
  // Product Gallery Loader
  // -----------------------------
  function initGalleryFromUrl() {
    const params = new URLSearchParams(window.location.search);

    const mediaId = params.get('media_id');
    const mediaIndex = parseInt(params.get('media'), 10);
    const openModal = params.get('modal') !== 'false';

    const isValidIndex = !isNaN(mediaIndex) && mediaIndex >= 0;
    if (!mediaId && !isValidIndex) return;

    retry(selectSlide, { attempts: 15, delay: 200 });

    function selectSlide() {
      const sliderElement =
        document.querySelector('.product-slideshow') ||
        document.querySelector('.flickity-enabled') ||
        document.querySelector('.flickity-slider') ||
        document.querySelector('.product-main-slide')?.parentElement;

      if (!sliderElement || !window.Flickity) return false;

      const flkty = Flickity.data(sliderElement);
      if (!flkty || !flkty.cells?.length) return false;

      let targetIndex = null;

      if (mediaId) {
        const slide = [...document.querySelectorAll('.product-main-slide')]
          .find(el => el.getAttribute('data-media-id') === mediaId);

        if (slide) {
          targetIndex = parseInt(slide.getAttribute('data-index'), 10);
        }
      } else {
        targetIndex = mediaIndex;
      }

      if (typeof targetIndex !== 'number' || isNaN(targetIndex)) {
        targetIndex = flkty.cells.length - 1;
      }

      targetIndex = Math.max(0, Math.min(targetIndex, flkty.cells.length - 1));

      flkty.select(targetIndex, false, true);

      if (openModal) {
        const slide = document.querySelector(
          `.product-main-slide[data-index="${targetIndex}"]`
        );

        const trigger = slide?.querySelector('.product-video-trigger');

        if (trigger) {
          setTimeout(() => trigger.click(), 100);
        }
      }

      return true;
    }
  }

  // -----------------------------
  // Flickity Sub-pixel Fix
  // -----------------------------
  function initFlickityWidthSnap() {
    const el = document.querySelector('.page-content--product .product-slideshow');
    if (!el) return;

    function snapWidth() {
      el.style.width = 'auto';
      const w = Math.floor(el.getBoundingClientRect().width);
      el.style.width = w + 'px';
      const flkty = Flickity.data(el);
      if (flkty) flkty.resize();
    }

    retry(() => {
      const flkty = Flickity.data(el);
      if (!flkty) return false;
      snapWidth();
      return true;
    }, { attempts: 20, delay: 150 });

    window.addEventListener('resize', snapWidth);
  }

  // -----------------------------
  // Product Carousel Component
  // -----------------------------
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.product-carousel-track');
      this.prev = this.querySelector('.product-carousel-nav-button--prev');
      this.next = this.querySelector('.product-carousel-nav-button--next');

      if (!this.track) return;

      this.bindEvents();
      this.update();
    }

    bindEvents() {
      this.prev?.addEventListener('click', () => this.scroll(-1));
      this.next?.addEventListener('click', () => this.scroll(1));

      this.track.addEventListener('scroll', () => this.update());
      window.addEventListener('resize', () => this.update());
    }

    scroll(direction) {
      const item = this.querySelector('.product-carousel-item');
      const amount = item
        ? item.offsetWidth + 20
        : this.track.offsetWidth * 0.8;

      this.track.scrollBy({
        left: direction * amount,
        behavior: 'smooth'
      });
    }

    update() {
      const atStart = this.track.scrollLeft <= 1;
      const atEnd =
        this.track.scrollLeft + this.track.offsetWidth >=
        this.track.scrollWidth - 1;

      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;

      const wrapper = this.querySelector('.product-carousel-wrapper');
      if (wrapper) {
        wrapper.classList.toggle('has-scroll-left', !atStart);
        wrapper.classList.toggle('has-scroll-right', !atEnd);
      }
    }
  }

  if (!customElements.get('product-carousel')) {
    customElements.define('product-carousel', ProductCarousel);
  }

  

  // -----------------------------
  // Init
  // -----------------------------
  function init() {
    initAfterSellStyles();
    initGalleryFromUrl();
    initFlickityWidthSnap();
    initGiftNote();
  }

  onReady(init);

  // Support Shopify dynamic sections
  document.addEventListener('shopify:section:load', init);

})();